// Metadata files dropped beside an image: which one pairs with which image,
// and what it says, to PREFILL the upload form. Nothing here is uploaded: the
// form's details go to the catalogue (enterprise/upload/store.ts), and the
// file itself stays on the person's machine.
//
// The pairing rules mirror the pipeline's FileSource (convert/…/sources/file.py):
// for an image `slide.svs` the companions are looked up by descending
// specificity over stems [slide, slide.svs] × suffixes below, first hit wins.

/** Ordered most-specific first — same table as the pipeline's FileSource. */
export const SIDECAR_SUFFIXES = [
  '.fhir.json',
  '.dicom.json',
  '.dcm.json',
  '.metadata.json',
  '.json',
] as const;

export function stemOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i <= 0 ? name : name.slice(0, i);
}

/** Candidate sidecar names for an image, in the pipeline's priority order. */
export function sidecarCandidates(imageName: string): string[] {
  const stems = [...new Set([stemOf(imageName), imageName])];
  const out: string[] = [];
  for (const stem of stems) {
    for (const suf of SIDECAR_SUFFIXES) out.push(`${stem}${suf}`);
  }
  return [...new Set(out)];
}

/** First dropped .json name that the pipeline would pair with this image. */
export function matchSidecar(imageName: string, jsonNames: string[]): string | undefined {
  const set = new Set(jsonNames);
  return sidecarCandidates(imageName).find((c) => set.has(c));
}

// ── the form draft ─────────────────────────────────────────────────────────
// One string per form field. The BASIC set is the everyday form; the ADVANCED
// set is the rest the catalogue keeps (issuers, the slide's acquisition, the
// scanner, coded specimen and procedure entries). enterprise/upload/details.ts
// maps it onto the server's image and study bodies.

export interface MetadataDraft {
  // basic — patient
  patientFamily: string;
  patientGiven: string;
  patientId: string;
  patientBirthDate: string; // YYYY-MM-DD
  patientSex: '' | 'M' | 'F' | 'O';
  /** PatientAge: "54", "54Y", "8M", "90+" (the pipeline coerces to AS form). */
  patientAge: string;
  // basic — study
  accession: string;
  studyDateTime: string; // YYYY-MM-DDTHH:MM (DateField)
  studyDescription: string;
  referringPhysician: string; // "Family, Given" or "Family^Given"
  // basic — the image
  /** Its Name: the Series Description, what the viewer's image cards show. A new file starts with its file's name, or (enterprise) its Specimen ID's (imageName.ts). */
  seriesDescription: string;
  /** The Type (data/imageTypes.ts); the server writes the modality from it. */
  imageType: string;
  /** Only what a file said (a DICOM header, a metadata sidecar); the form has no field for it. */
  modality: string;
  // basic — specimen
  containerId: string;
  specimenId: string;
  // advanced — identifiers
  patientIdIssuer: string;
  accessionIssuer: string;
  studyId: string;
  // advanced — the slide
  /** ObjectiveLensPower (0048,0112), WSI only: only what a metadata file said; the form has no field for it (ingest reads the scanner's). */
  objectivePower: string;
  /** IlluminationTypeCodeSequence (0022,0016), CID 8123: read from the server, never sent (the Type owns it). */
  illuminationValue: string;
  illuminationScheme: string;
  illuminationMeaning: string;
  // advanced — equipment
  manufacturer: string;
  stationName: string;
  deviceSerial: string;
  // advanced — specimen detail
  containerIdIssuer: string;
  specimenDescription: string;
  specimenTypeValue: string;
  specimenTypeScheme: string;
  specimenTypeMeaning: string;
  anatomicSiteValue: string;
  anatomicSiteScheme: string;
  anatomicSiteMeaning: string;
  /** L, R or B (CID 244); nothing without an anatomic site. */
  laterality: string;
  // advanced — requested procedure
  procedureValue: string;
  procedureScheme: string;
  procedureMeaning: string;
}

export const EMPTY_DRAFT: MetadataDraft = {
  patientFamily: '',
  patientGiven: '',
  patientId: '',
  patientBirthDate: '',
  patientSex: '',
  patientAge: '',
  accession: '',
  studyDateTime: '',
  studyDescription: '',
  referringPhysician: '',
  imageType: '',
  modality: '',
  containerId: '',
  specimenId: '',
  patientIdIssuer: '',
  accessionIssuer: '',
  studyId: '',
  seriesDescription: '',
  objectivePower: '',
  illuminationValue: '',
  illuminationScheme: '',
  illuminationMeaning: '',
  manufacturer: '',
  stationName: '',
  deviceSerial: '',
  containerIdIssuer: '',
  specimenDescription: '',
  specimenTypeValue: '',
  specimenTypeScheme: '',
  specimenTypeMeaning: '',
  anatomicSiteValue: '',
  anatomicSiteScheme: '',
  anatomicSiteMeaning: '',
  laterality: '',
  procedureValue: '',
  procedureScheme: '',
  procedureMeaning: '',
};

/** The advanced-only keys — used to auto-open the Advanced section when a
 *  prefill populated any of them. */
export const ADVANCED_FIELDS = [
  'patientIdIssuer',
  'accessionIssuer',
  'studyId',
  'manufacturer',
  'stationName',
  'deviceSerial',
  'containerIdIssuer',
  'specimenDescription',
  'specimenTypeValue',
  'specimenTypeScheme',
  'specimenTypeMeaning',
  'anatomicSiteValue',
  'anatomicSiteScheme',
  'anatomicSiteMeaning',
  'laterality',
  'procedureValue',
  'procedureScheme',
  'procedureMeaning',
] as const satisfies readonly (keyof MetadataDraft)[];

/**
 * Field-wise draft merge: `primary` (e.g. a companion metadata file) wins
 * wherever it has a value; gaps keep `secondary` (e.g. a DICOM file's embedded
 * tags, or the form's defaults). Mirrors the pipeline's merge semantics.
 */
export function mergeDrafts(primary: MetadataDraft, secondary: MetadataDraft): MetadataDraft {
  const out = { ...secondary };
  for (const k of Object.keys(EMPTY_DRAFT) as (keyof MetadataDraft)[]) {
    const v = primary[k];
    if (v.trim() !== '') (out as Record<string, string>)[k] = v;
  }
  return out;
}

// ── prefill parsing ────────────────────────────────────────────────────────
// Best-effort, prefill-only: the three formats the pipeline accepts. Anything
// not mapped is simply not prefilled; the person reviews the form either way.

type Json = Record<string, unknown>;

function asRecord(v: unknown): Json | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : null;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
}

/** "20240117" or ISO → YYYY-MM-DD (input[type=date] value). */
export function toDateInput(v: string): string {
  const t = v.trim();
  if (/^\d{8}$/.test(t)) return `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)}`;
  const m = t.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

export function toSex(v: string): MetadataDraft['patientSex'] {
  const t = v.trim().toLowerCase();
  if (t === 'm' || t === 'male') return 'M';
  if (t === 'f' || t === 'female') return 'F';
  if (t === 'o' || t === 'other') return 'O';
  return '';
}

/** "Family^Given^…" → [family, given]. */
export function splitPn(v: string): [string, string] {
  const [family = '', given = ''] = v.split('^');
  return [family, given];
}

function codeToDraft(v: unknown): { value: string; scheme: string; meaning: string } {
  const c = asRecord(v);
  return {
    value: str(c?.value),
    scheme: str(c?.scheme),
    meaning: str(c?.meaning),
  };
}

export type SidecarFormat = 'simple' | 'dicom-json' | 'fhir';

export function detectFormat(payload: Json): SidecarFormat {
  if (payload.resourceType === 'Bundle' || Array.isArray(payload.entry)) return 'fhir';
  const keys = Object.keys(payload);
  if (keys.length > 0 && keys.every((k) => /^[0-9A-Fa-f]{8}$/.test(k))) return 'dicom-json';
  return 'simple';
}

function fromSimple(p: Json, d: MetadataDraft): void {
  const name = p.patientName;
  if (typeof name === 'string') {
    [d.patientFamily, d.patientGiven] = splitPn(name);
  } else {
    const n = asRecord(name);
    if (n) {
      d.patientFamily = str(n.family);
      d.patientGiven = str(n.given);
    }
  }
  d.patientId = str(p.patientId);
  d.patientIdIssuer = str(p.patientIdIssuer);
  d.patientBirthDate = toDateInput(str(p.patientBirthDate));
  d.patientSex = toSex(str(p.patientSex));
  d.patientAge = str(p.patientAge);
  d.accession = str(p.accession);
  d.accessionIssuer = str(p.accessionIssuer);
  d.studyId = str(p.studyId);
  d.studyDescription = str(p.studyDescription);
  d.referringPhysician = str(p.referringPhysician).replace('^', ', ');
  d.modality = str(p.modality) || d.modality;
  d.seriesDescription = str(p.seriesDescription);
  d.objectivePower = str(p.objectivePower);
  const il = codeToDraft(p.illuminationType);
  d.illuminationValue = il.value;
  d.illuminationScheme = il.scheme;
  d.illuminationMeaning = il.meaning;
  d.manufacturer = str(p.manufacturer);
  d.stationName = str(p.stationName);
  d.deviceSerial = str(p.deviceSerial);
  d.containerId = str(p.containerId);
  d.containerIdIssuer = str(p.containerIdIssuer);
  const specimens = Array.isArray(p.specimens) ? p.specimens : [];
  const s0 = asRecord(specimens[0]);
  if (s0) {
    d.specimenId = str(s0.id);
    d.specimenDescription = str(s0.description);
    const t = codeToDraft(s0.type);
    d.specimenTypeValue = t.value;
    d.specimenTypeScheme = t.scheme;
    d.specimenTypeMeaning = t.meaning;
    const a = codeToDraft(s0.anatomicSite);
    d.anatomicSiteValue = a.value;
    d.anatomicSiteScheme = a.scheme;
    d.anatomicSiteMeaning = a.meaning;
  }
  const proc = codeToDraft(p.requestedProcedure);
  d.procedureValue = proc.value;
  d.procedureScheme = proc.scheme;
  d.procedureMeaning = proc.meaning;
  const dt = str(p.studyDateTime) || str(p.studyStarted);
  if (dt) d.studyDateTime = dt.slice(0, 16);
}

/** One DICOM-JSON attribute's first Value, unwrapping PN Alphabetic. */
function dcmValue(p: Json, tag: string): string {
  const attr = asRecord(p[tag]);
  const v = Array.isArray(attr?.Value) ? attr.Value[0] : undefined;
  const pn = asRecord(v);
  if (pn && typeof pn.Alphabetic === 'string') return pn.Alphabetic;
  return str(v);
}

function fromDicomJson(p: Json, d: MetadataDraft): void {
  [d.patientFamily, d.patientGiven] = splitPn(dcmValue(p, '00100010'));
  d.patientId = dcmValue(p, '00100020');
  d.patientBirthDate = toDateInput(dcmValue(p, '00100030'));
  d.patientSex = toSex(dcmValue(p, '00100040'));
  d.accession = dcmValue(p, '00080050');
  d.studyDescription = dcmValue(p, '00081030');
  d.referringPhysician = dcmValue(p, '00080090').replace('^', ', ');
  d.modality = dcmValue(p, '00080060') || d.modality;
  d.containerId = dcmValue(p, '00400512');
  d.manufacturer = dcmValue(p, '00080070');
  d.stationName = dcmValue(p, '00081010');
  d.deviceSerial = dcmValue(p, '00181000');
  const date = dcmValue(p, '00080020');
  const time = dcmValue(p, '00080030');
  if (/^\d{8}$/.test(date)) {
    const hhmm = /^\d{4}/.test(time) ? `${time.slice(0, 2)}:${time.slice(2, 4)}` : '00:00';
    d.studyDateTime = `${toDateInput(date)}T${hhmm}`;
  }
}

function fromFhir(p: Json, d: MetadataDraft): void {
  const entries = Array.isArray(p.entry) ? p.entry : [];
  const resources = entries
    .map((e) => asRecord(asRecord(e)?.resource))
    .filter((r): r is Json => r !== null);
  const byType = (t: string) => resources.find((r) => r.resourceType === t);

  const patient = byType('Patient');
  if (patient) {
    const name = asRecord(Array.isArray(patient.name) ? patient.name[0] : undefined);
    if (name) {
      d.patientFamily = str(name.family);
      const given = Array.isArray(name.given) ? name.given : [];
      d.patientGiven = given.map(str).filter(Boolean).join(' ');
    }
    d.patientBirthDate = toDateInput(str(patient.birthDate));
    d.patientSex = toSex(str(patient.gender));
    const ident = asRecord(
      Array.isArray(patient.identifier) ? patient.identifier[0] : undefined,
    );
    if (ident) d.patientId = str(ident.value);
  }

  const sr = byType('ServiceRequest') ?? byType('DiagnosticReport') ?? byType('ImagingStudy');
  if (sr) {
    const ident = asRecord(Array.isArray(sr.identifier) ? sr.identifier[0] : undefined);
    if (ident) d.accession = str(ident.value);
  }

  const specimen = byType('Specimen');
  if (specimen) {
    const acc = asRecord(specimen.accessionIdentifier);
    d.specimenId = str(
      asRecord(Array.isArray(specimen.identifier) ? specimen.identifier[0] : undefined)?.value,
    );
    if (!d.accession && acc) d.accession = str(acc.value);
    const container = asRecord(
      Array.isArray(specimen.container) ? specimen.container[0] : undefined,
    );
    const cid = asRecord(container?.identifier);
    if (cid) d.containerId = str(cid.value);
  }
}

/**
 * Parse a dropped metadata file's JSON into a prefilled draft. Returns the
 * detected format alongside, so the form can say what it recognized.
 */
export function parseSidecar(
  payload: unknown,
): { format: SidecarFormat; draft: MetadataDraft } | null {
  const p = asRecord(payload);
  if (!p) return null;
  const draft: MetadataDraft = { ...EMPTY_DRAFT };
  const format = detectFormat(p);
  if (format === 'fhir') fromFhir(p, draft);
  else if (format === 'dicom-json') fromDicomJson(p, draft);
  else fromSimple(p, draft);
  return { format, draft };
}
